import { faPlayCircle } from "@fortawesome/pro-solid-svg-icons/faPlayCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, CardContent, CircularProgress, Dialog, DialogContent, Divider, Grid, Menu, MenuItem, Typography } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedClassKey, GlobalClassKeys, TimelineClassKey } from "assets/jss/common-styles";
import ButtonEllipsis from "components/Buttons/ButtonEllipsis";
import IconButtonEdit from "components/Buttons/IconButtonEdit";
import DrawerTextInput from "components/Dialogs/DrawerTextInput";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiPaper from "components/MuiExtras/MuiPaper";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import * as FavoriteUtils from "lib/FavoriteUtils";
import { FeedType, ListType } from "models/ApiEnums";
import { FileThumbnaildGetDto } from "models/ApiModels";
import * as React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import RenderVideo from "views/Publication/Components/RenderVideo";
import PublicationRelationshipsDialog from "views/Publication/Dialogs/PublicationRelationshipsDialog";
import ActionsDefaultRight from "views/Timeline/Components/CardComp/ActionsDefaultRight";
import ActionsSocial from "views/Timeline/Components/CardComp/ActionsSocial";
import BodyTextExpand from "views/Timeline/Components/CardComp/BodyTextExpand";
import HeaderLine from "views/Timeline/Components/CardComp/HeaderLine";
import IconsLine from "views/Timeline/Components/CardComp/IconsLine";
import Publisher from "views/Timeline/Components/CardComp/Publisher";
import StatusLine from "views/Timeline/Components/CardComp/StatusLine";
import { CardBodyData } from "views/Timeline/Components/CardLib/CardBodyData";
import { CardCommonActions } from "views/Timeline/Components/CardLib/CardCommonActions";
import { ICardBaseProps } from "views/Timeline/Components/CardLib/ICardProps";
import Edit from "views/Timeline/Dialogs/PublicationEdit";
import PublicationLikesReviewDrawer from "views/Timeline/Dialogs/PublicationLikesReviewDrawer";
import ReorderListDialog from "views/Timeline/Dialogs/ReorderListDialog";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    mouseOver: boolean;
    menuAnchorEl: any;
    orderByMenuAnchorEl: any;
    dialog: any;
}

interface IProps {
    onFeedResetRequest: () => void;
}

type Props = IProps & ICardBaseProps & WithSnackbar & WithTranslation & StyledComponentProps<FeedClassKey | TimelineClassKey>;

/////////////////////////////////////////////////////////////////////////////////////////////////

export class ListCardHeader extends React.Component<Props, IState> {
    private commonActions: CardCommonActions;

    constructor(props: Props) {
        super(props);

        this.state = {
            mouseOver: false,
            menuAnchorEl: null,
            orderByMenuAnchorEl: null,
            dialog: null,
        };

        this.commonActions = new CardCommonActions(props.onUpdateCard, props.snackbar.showMessage);
    }

    ///
    private handleOpenMenu(e: React.MouseEvent<HTMLElement>) {
        this.setState({ menuAnchorEl: e.currentTarget });
    }

    ///
    private handleCloseMenu() {
        this.setState({ menuAnchorEl: null });
    }

    ///
    private handleCloseDialog(): void {
        this.setState({ dialog: null });
    }

    ///
    private handleReorder() {
        this.handleCloseMenu();
        this.setState({
            dialog: <ReorderListDialog
                listId={this.props.item.publicationId}
                onClose={
                    () => {
                        this.setState({ dialog: null });
                        this.props.onFeedResetRequest();
                    }
                }
            />
        });
    }

    ///
    private handleClear() {
        this.handleCloseMenu();
        this.setState({
            dialog: (
                <MuiConfirmation
                    title={this.props.t("Common.Feed.List.ConfirmationCleanupTitle")}
                    message={this.props.t("Common.Feed.List.ConfirmationCleanupMessage")}
                    onClose={
                        (result) => {
                            if (result) {
                                api.listClear(this.props.item.publicationId)
                                    .then(() => {
                                        this.props.snackbar.showMessage(this.props.t("Common.Feed.Messages.ListCleanupSuccess"), "success");
                                        this.props.onFeedResetRequest();
                                    });
                            }
                            this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    }

    ///
    private handleEdit = () => {
        const { item } = this.props;

        if (this.props.onBeforeEdit) {
            if (!this.props.onBeforeEdit()) return;
        }

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <Edit
                    data={item}
                    onClose={
                        (update: boolean) => {
                            if (update) {
                                this.props.onReloadPublication();
                            }
                            this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    };

    ///
    private handleDelete() {
        this.handleCloseMenu();

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <MuiConfirmation
                    title={this.props.t("Common.Feed.List.ConfirmationDeleteTitle")}
                    message={this.props.t("Common.Feed.List.ConfirmationDeleteMessage")}
                    onClose={
                        (result) => {
                            if (result) {
                                this.props.onRemovePublication();
                            }
                            this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    }

    ///
    private handleLikeReviewClick() {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationLikesReviewDrawer
                publicationId={this.props.item.publicationId}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    }

    ///
    private handleRelationships() {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationRelationshipsDialog
                publicationId={this.props.item.publicationId}
                publicationTitle={this.props.item.publication.title}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    }

    //
    private handleInfo() {
        this.setState({ menuAnchorEl: null }, () => this.props.onInfoRequest());
    }

    ///
    private handleBlock = () => {
        const { t, item: { publicationId } } = this.props;

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <DrawerTextInput
                    title={t("Common.Feed.Actions.BlockTitle")}
                    placeHolder={t("Common.Feed.Actions.BlockPlaceholder")}
                    confirmText={t("Common.Feed.Actions.BlockConfirmButton")}
                    rows={5}
                    maxLength={1500}
                    inputOptional={false}
                    onCancel={() => this.handleCloseDialog()}
                    onAccept={
                        (text) => {
                            api.publicationSwapBlock(publicationId, text)
                                .then((result) => {
                                    if (result) {
                                        this.props.onReloadPublication();
                                        this.props.snackbar.showMessage(t("Common.Feed.Messages.BlockSuccess"), "success");
                                    }
                                    this.handleCloseDialog();
                                });
                        }
                    }
                />
            )
        });
    };

    ///
    private handleUnblock = () => {
        const { t, item: { publicationId, publication: { title } } } = this.props;

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <MuiConfirmation
                    title={t("Common.Feed.Actions.UnblockTitle")}
                    message={
                        <Trans
                            i18nKey="Common.Feed.Actions.UnblockMessage"
                            values={{ title }}
                            components={{ b: <strong /> }}
                        />
                    }
                    onClose={
                        (confirm) => {
                            if (confirm) {
                                api.publicationSwapBlock(publicationId, null)
                                    .then((result) => {
                                        if (result) {
                                            this.props.onReloadPublication();
                                            this.props.snackbar.showMessage(t("Common.Feed.Messages.UnblockSuccess"), "success");
                                        }
                                        this.handleCloseDialog();
                                    });

                            }
                            else
                                this.handleCloseDialog();
                        }
                    }
                />
            )
        });
    };

    ///
    private showVideo(attach: FileThumbnaildGetDto) {
        this.setState({
            dialog:
                <Dialog
                    open
                    fullWidth
                    onClose={
                        () => this.setState({ dialog: null })
                    }
                >
                    <DialogContent>
                        <RenderVideo file={attach.mainFile} autoplay={true} />
                    </DialogContent>
                </Dialog>
        });
    }

    ///
    private buildOverMenu() {
        const { t, item: { publication, permissions } } = this.props;
        const { menuAnchorEl } = this.state;
        var items: Array<JSX.Element> = [];

        if (publication.list.listType == ListType.Custom) {
            if (permissions.canEdit)
                items.push(<MenuItem key="edit" onClick={() => { this.handleEdit(); }}><span>{t("Common.MenuAction.Edit")}</span></MenuItem>);


            if (permissions.canBlock) {
                if (publication.blocked)
                    items.push(<MenuItem key="unblock" onClick={this.handleUnblock}>{t("Common.MenuAction.Unblock")}</MenuItem>);
                else
                    items.push(<MenuItem key="block" onClick={this.handleBlock}>{t("Common.MenuAction.Block")}</MenuItem>);
            }

            if (permissions.canEditRelationships)
                items.push(<MenuItem key="relationship" onClick={() => this.handleRelationships()}>{t("Common.MenuAction.EditRelationships")}</MenuItem>);

            if (permissions.canEdit) {
                items.push(<Divider key="div1" />);
                items.push(<MenuItem key="reorder" onClick={() => { this.handleReorder(); }}><span>{t("Common.MenuAction.Reorder")}</span></MenuItem>);
                items.push(<MenuItem key="clean" onClick={() => { this.handleClear(); }}><span>{t("Common.MenuAction.Reset")}</span></MenuItem>);
            }

            if (items.length > 0) items.push(<Divider key="div2" />);
            items.push(<MenuItem key="likeReview" onClick={() => { this.handleLikeReviewClick(); }}><span>{t("Common.MenuAction.LikeReview")}</span></MenuItem>);

            if (permissions.canDelete) {
                if (items.length > 0) items.push(<Divider key="div4" />);
                items.push(<MenuItem key="remove" onClick={() => { this.handleDelete(); }}><MuiColor variant="error">{t("Common.MenuAction.Delete")}</MuiColor></MenuItem>);
            }
        }

        if (this.props.onInfoRequest) {
            if (items.length > 0) items.push(<Divider key="div3" />);
            items.push(<MenuItem key="info" onClick={() => { this.handleInfo(); }}><span>{t("Common.MenuAction.Info")}</span></MenuItem>);
        }

        if (items.length == 0)
            return null;

        return (
            <>
                <ButtonEllipsis
                    size="small"
                    onClick={(e) => { this.handleOpenMenu(e); }}
                />
                <Menu
                    id="simple-menu"
                    anchorEl={menuAnchorEl}
                    open={menuAnchorEl != null}
                    onClose={() => { this.handleCloseMenu(); }}
                >
                    {items}
                </Menu>
            </>
        );
    }

    ///
    public render() {
        const { classes, item } = this.props;
        const fullView = true;

        if (item == null)
            return (
                <MuiPaper>
                    <Grid container direction="row" alignContent="space-between" >
                        <Grid item>
                            <CircularProgress size={60} />
                        </Grid>
                    </Grid>
                </MuiPaper>
            );

        const { item: { body, publication, permissions } } = this.props;

        const bodyData = new CardBodyData(this.props.item, true, false);
        const attach = body.attachs && body.attachs.length == 1 ? body.attachs[0] : null;
        let img: JSX.Element = (<MuiColor variant="muted"><FontAwesomeIcon icon={FavoriteUtils.getIcon(publication.list.listType, publication.isPublic)} fixedWidth size="3x" /></MuiColor>);

        if (attach) {

            if (attach.mainFile.mimeType.indexOf("image") > -1)
                img = (<img style={{ width: 118 }} src={attach.mainFile.url} alt={"List Thumbnail"} />);

            if (attach.mainFile.mimeType.indexOf("video") > -1)
                img = (
                    <a
                        href={null}
                        className={classes.videoLinkImage}
                        onClick={() => { this.showVideo(attach); }}
                    >
                        {
                            attach.thumbnail == null
                                ? <FontAwesomeIcon icon={faPlayCircle} fixedWidth size="3x" />
                                : img
                        }
                    </a>
                );
        }

        const overMenu = this.buildOverMenu();

        return (
            <Card className={classes.card}
                onMouseEnter={() => this.setState({ mouseOver: true })}
                onMouseLeave={() => this.setState({ mouseOver: false })}
            >
                <CardContent>
                    {this.state.dialog}

                    {
                        publication.isPublic &&
                        <Grid container justifyContent="center" alignItems="center">
                            <Grid item xs>
                                { /* Area 1 */}
                                <HeaderLine
                                    classes={classes}
                                    data={item}
                                    feedType={FeedType.List}
                                    fullView={fullView}
                                    inApprovalMode={false}
                                    mouseOver={this.state.mouseOver}
                                    textExpanded={item.body.fullContentLoaded}
                                    topMessageMode="default"
                                    onUpdateCard={(item) => this.props.onUpdateCard(item)}
                                />
                            </Grid>
                            <Grid item>
                                { /* Area 2 */}
                                <IconsLine
                                    data={item}
                                    feedType={FeedType.List}
                                    mouseOver={this.state.mouseOver}
                                    textExpanded={item.body.fullContentLoaded}
                                    classes={classes}
                                    onEditPublication={this.handleEdit}
                                    onRemovePublication={null}
                                    onViewLater={item.permissions.canViewLater ? this.commonActions.viewLater : null}
                                    onTextExpandedChange={(textExpanded) => this.commonActions.expandText(item, textExpanded)}
                                />
                            </Grid>
                        </Grid>
                    }

                    <Grid container
                        spacing={3}
                        wrap="nowrap"
                        alignItems="flex-start"
                    >
                        <Grid item>
                            {img}
                        </Grid>

                        <Grid item xs >

                            <Typography variant="h6">
                                <span>{publication.title}</span>
                            </Typography>

                            <Publisher
                                classes={this.props.classes}
                                data={this.props.item}
                                feedType={FeedType.List}
                            />

                            <div className={classes.cardBody}>
                                <StatusLine data={this.props.item} feedType={FeedType.List} />

                                <BodyTextExpand
                                    classes={classes}
                                    text={bodyData.content}
                                    expanded={bodyData.textExpanded}
                                    onTextExpandedChange={(textExpanded) => this.commonActions.expandText(item, textExpanded)}
                                />
                            </div>

                        </Grid>

                        {
                            item.publication.list.listType === ListType.Custom && this.props.item.publication.isPublic === false && permissions.canEdit &&
                            <Grid item>
                                <IconButtonEdit onClick={() => this.handleEdit()} size="small" visible={this.state.mouseOver} />
                            </Grid>
                        }
                    </Grid>

                    {
                        <Grid container justifyContent="space-between" alignItems="center" className={classes.cardActions}>
                            {
                                publication.isPublic
                                    ? (
                                        <>
                                            <Grid item>
                                                <ActionsSocial
                                                    data={this.props.item}
                                                    fullView={fullView}
                                                    showCommentsButton={true}
                                                    onUpdateCard={(item) => this.props.onUpdateCard(item)}
                                                />
                                            </Grid>

                                            {
                                                overMenu != null &&
                                                <Grid item>
                                                    <ActionsDefaultRight
                                                        classes={classes}
                                                        data={this.props.item}
                                                        fullView={fullView}
                                                        showAddToFavorites
                                                        showShareButton
                                                        showRelationsButton={this.props.showRelationsButton}
                                                        onUpdateCard={(item) => this.props.onUpdateCard(item)}
                                                        onSidebarResetRequest={() => this.props.onSidebarResetRequest()}
                                                    />

                                                    {this.buildOverMenu()}

                                                </Grid>
                                            }
                                        </>
                                    )
                                    : overMenu != null
                                        ? (
                                            <Grid item xs className={GlobalClassKeys.textRight}>
                                                {this.buildOverMenu()}
                                            </Grid>
                                        )
                                        : null
                            }
                        </Grid>
                    }

                </CardContent>
            </Card >
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

export default withSnackbar(withTranslation()(ListCardHeader));